type EventValue = {
  type?: string;
  value?: string;
};

// Duck-typed on purpose — mirrors shouldDropHandledGraphql500's SentryEventLike
// so tests can pass plain object literals with no @sentry/* mocking.
type SentryEventLike = {
  message?: string;
  tags?: Record<string, string | undefined>;
  contexts?: {
    response?: {
      status_code?: number;
    };
  };
  exception?: {
    values?: EventValue[];
  };
};

// Apollo's own error classes. A 401 that surfaces as one of these came through
// the HttpLink/errorLink chain, which already owns the 401 contract (retry with
// a re-read token, then global logout). Anything else carrying a 401 — a bare
// fetch, a REST helper — is not covered by that contract and must still report.
const APOLLO_ERROR_TYPES = new Set(['ApolloError', 'ServerError']);

const hasApolloErrorType = (event: SentryEventLike): boolean =>
  event.exception?.values?.some(
    (value) => !!value.type && APOLLO_ERROR_TYPES.has(value.type)
  ) ?? false;

const is401 = (event: SentryEventLike): boolean => {
  if (event.contexts?.response?.status_code === 401) return true;

  const statusCodeTag =
    event.tags?.status_code ??
    event.tags?.http_status_code ??
    event.tags?.['http.status_code'];
  if (statusCodeTag && Number(statusCodeTag) === 401) return true;

  // Apollo's ServerError message is the only place the status appears for an
  // HTTP 401: the response body is not GraphQL, so there are no graphQLErrors
  // and Sentry gets no response context. See useApolloClient.tsx's comment on
  // isPermissionScopedError.
  return [
    event.message,
    ...(event.exception?.values?.map((value) => value.value ?? '') ?? []),
  ].some((text) => !!text && /status code 401\b/i.test(text));
};

/**
 * Drops the `ApolloError: Response not successful: Received status code 401`
 * exception events (Sentry FRONTEND-2YY, TA-10850).
 *
 * A 401 means the session ended. `errorLink` already handles it end to end —
 * it retries once with a re-read token to cover the startup race, and on a
 * second 401 calls `setRecentStatusCode(401)`, which logs the user out and
 * redirects to `/login`. Every query still in flight on the abandoned route
 * then fails with the same 401, so one expired session produces a burst of
 * identical exception events attributed to whichever route the user was on.
 *
 * These carry no signal we don't already have, and no context to act on: the
 * stack is pure `zen-observable`/`@apollo` frames, the `url` is `/login` (the
 * page the app had already navigated to by the time the event was sent), and
 * the transaction is the stale pre-logout route. The unrecoverable 401 is
 * reported deliberately and with the operation name attached, via
 * `Sentry.captureMessage('401 on <op> — no token, logging out')` in
 * `useApolloClient.tsx` — that is the event worth alerting on, and it is
 * unaffected by this filter.
 *
 * Deliberately narrow: only Apollo's own error types, and only 401. A 401 from
 * a bare `fetch` or a REST helper is not covered by the contract above and
 * still reports.
 *
 * Known trade-off (PR #5885 review): a permission-scoped 401 — one whose body
 * carries FORBIDDEN / PERMISSION_DENIED / NOT_AUTHORIZED — is dropped by this
 * filter too. `errorLink` treats those as valid-token, operation-level errors
 * and returns without retrying or logging out, but returning only skips that
 * handling; the ApolloError still propagates, so it lands here looking
 * identical to session 401s. The event carries no response body, so there is
 * nothing in it to tell the two apart.
 *
 * Accepted because the loss is small and the failure is not silent:
 * PERMISSION_DENIED is already in `IGNORED_ERROR_CODES`, so only FORBIDDEN and
 * NOT_AUTHORIZED lose exception coverage, and both still reach the user as an
 * operation-level error on the screen that triggered them rather than being
 * swallowed. Distinguishing them here would mean tagging the event at capture
 * time, which is not possible while the capture site is outside our code.
 */
export const shouldDropHandledApollo401 = (event: SentryEventLike): boolean => {
  return hasApolloErrorType(event) && is401(event);
};
